import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { useQuickActions } from './useQuickActions';
import { MAX_ACTIONS, DEFAULT_ACTION_IDS, type DashboardActionCommand } from './dashboardActions';
import './QuickActions.css';

/**
 * The dashboard's action row.
 *
 * The first control on this screen that does anything. Which four (or eight)
 * appear is the user's choice, because the three jobs this dashboard serves —
 * diet tech, kitchen, dietitian — do not open the same things, and a single
 * fixed set would be wrong for two of them.
 *
 * The first action is filled and the rest are quiet, following the same tier
 * rule as `PageToolbar`: at most one thing on a screen gets to be the primary.
 */
export default function QuickActions({
  savedIds,
  onChange,
  onCommand,
}: {
  /** Pinned ids, or null when the user has never chosen. */
  savedIds: string[] | null | undefined;
  onChange: (ids: string[]) => void;
  onCommand: (command: DashboardActionCommand) => void;
}) {
  const navigate = useNavigate();
  const { pinned, available } = useQuickActions(savedIds);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const pinnedIds = pinned.map((a) => a.id);

  const toggle = (id: string) => {
    const next = pinnedIds.includes(id)
      ? pinnedIds.filter((x) => x !== id)
      : [...pinnedIds, id].slice(0, MAX_ACTIONS);
    onChange(next);
  };

  return (
    <div className="quick-actions" data-tour="quick-actions">
      {pinned.map((a, i) => (
        <button
          key={a.id}
          type="button"
          className={i === 0 ? 'qa-btn qa-btn--primary' : 'qa-btn'}
          onClick={() => (a.command ? onCommand(a.command) : navigate(a.route!))}
        >
          <i className={`bi ${a.icon}`} aria-hidden="true" />
          <span>{a.displayLabel}</span>
        </button>
      ))}

      {pinned.length === 0 && (
        <span className="qa-empty">No quick actions pinned</span>
      )}

      {/* Named by its visible text ("Add"); the tooltip describes it. */}
      <Tooltip title="Choose quick actions" describeChild>
        <button
          type="button"
          className="qa-btn qa-btn--add"
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-haspopup="true"
        >
          <i className="bi bi-plus-lg" aria-hidden="true" />
          <span>Add</span>
        </button>
      </Tooltip>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {available.map((a) => {
          const on = pinnedIds.includes(a.id);
          return (
            <MenuItem
              key={a.id}
              onClick={() => toggle(a.id)}
              // Full is full — but an already-pinned item must stay clickable
              // so it can be removed.
              disabled={!on && pinnedIds.length >= MAX_ACTIONS}
            >
              <Checkbox size="small" checked={on} sx={{ p: 0, mr: 1 }} />
              <ListItemIcon sx={{ minWidth: 30 }}>
                <i className={`bi ${a.icon}`} aria-hidden="true" />
              </ListItemIcon>
              <ListItemText>{a.displayLabel}</ListItemText>
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem
          onClick={() => {
            onChange(DEFAULT_ACTION_IDS);
            setAnchor(null);
          }}
        >
          <ListItemIcon sx={{ minWidth: 30 }}>
            <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
          </ListItemIcon>
          <ListItemText>Reset to defaults</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
}
